import {
  IonIcon,
  IonItem,
  IonList,
  IonMenu,
  IonLabel,
  IonTitle,
  IonHeader,
  IonToolbar,
  IonContent,
  IonPopover,
  IonListHeader,
  MenuController,
  AlertController,
  ModalController,
  PopoverController,
} from '@ionic/angular/standalone';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY } from 'rxjs';
import { addIcons } from 'ionicons';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { isNil } from 'lodash-es';
import {
  pricetagsOutline,
  checkmarkCircle,
  downloadOutline,
  logoWhatsapp,
  lockClosed,
  download,
  settings,
  logOut,
  person,
  apps,
  call,
  ban,
} from 'ionicons/icons';

import { APP_ROUTES } from '@app/app-routes.const';
import { Events } from '@app/services/events.service';
import { UserService } from '@app/services/user.service';
import { ToastService } from '@app/services/toast.service';
import { StatusService } from '@app/services/status.service';
import { UploadService } from '@app/services/upload.service';
import { LabelsService } from '@app/services/labels.service';
import { LoadingService } from '@app/services/loading.service';
import { HOME_ROUTES } from '@app/components/tabs/tabs.routes';
import { DownloadService } from '@app/services/download.service';
import { ActiveEndpoint } from '@app/interfaces/active-endpoint.interface';
import { StatusVariables } from '@app/interfaces/current-status.interface';
import { ActiveEndpointService } from '@app/services/active-endpoint.service';
import { EndpointGroupsInterface } from '@app/interfaces/endpoint-groups.interface';
import { SelectActiveEndpointComponent } from '@app/components/select-active-endpoint/select-active-endpoint.component';
import { ImportAddressesResultComponent } from '@app/components/import-addresses-result/import-addresses-result.component';

@Component({
  selector: 'app-menu',
  templateUrl: 'menu.component.html',
  styleUrls: ['menu.component.scss'],
  imports: [
    IonListHeader,
    IonToolbar,
    IonContent,
    IonPopover,
    IonHeader,
    IonTitle,
    IonLabel,
    IonMenu,
    IonIcon,
    IonList,
    IonItem,
  ],
})
export class MenuComponent {
  userSrv = inject(UserService);
  private router = inject(Router);
  private events = inject(Events);
  private toastSrv = inject(ToastService);
  private labelsSrv = inject(LabelsService);
  private statusSrv = inject(StatusService);
  private uploadSrv = inject(UploadService);
  private menuCtrl = inject(MenuController);
  private alertCtrl = inject(AlertController);
  private loadingSrv = inject(LoadingService);
  private modalCtrl = inject(ModalController);
  private downloadSrv = inject(DownloadService);
  private popoverCtrl = inject(PopoverController);
  private activeEndpointSrv = inject(ActiveEndpointService);

  activeEndpoint = toSignal(this.activeEndpointSrv.activeEndpoint$);
  statuses = toSignal(this.statusSrv.status$);
  isDesktop = !Capacitor.isNativePlatform();

  constructor() {
    addIcons({
      ban,
      call,
      apps,
      person,
      logOut,
      settings,
      download,
      lockClosed,
      logoWhatsapp,
      downloadOutline,
      checkmarkCircle,
      pricetagsOutline,
    });

    this.statusSrv.status$.pipe(takeUntilDestroyed()).subscribe((statuses) => {
      if (isNil(statuses.endpoint_group)) {
        return;
      }
      if (parseInt(statuses.endpoint_group) !== this.activeEndpoint()?.id) {
        this.activeEndpointSrv
          .getGroups(parseInt(statuses.endpoint_group))
          .pipe(
            catchError((error) => {
              console.log(`error get endpoint group #${statuses.endpoint_group}`, error);
              return EMPTY;
            }),
          )
          .subscribe((group) => {
            if (Array.isArray(group) && group.length) {
              this.activeEndpointSrv.setActiveEndpoint(group[0]);
            }
          });
      }
    });
  }

  changeActiveEndpoint($event: MouseEvent) {
    this.loadingSrv.runWithLoading(this.activeEndpointSrv.getGroups()).subscribe({
      next: (groups) => this.showEndpointPopover(groups, $event),
      error: () => this.toastSrv.showToast('Error set endpoint groups'),
    });
  }

  async showEndpointPopover(groups: ActiveEndpoint[], event: Event) {
    const popover = await this.popoverCtrl.create({
      component: SelectActiveEndpointComponent,
      componentProps: {
        activeEndpoint: this.activeEndpoint(),
        endpointGroups: groups,
      },
      event,
    });
    await popover.present();

    const item = await popover.onDidDismiss();
    if (item?.data?.GROUP_ID) {
      const statuses: StatusVariables[] = [{ VAR_NAME: 'endpoint_group', VAR_VALUE: item.data.GROUP_ID }];
      if (this.pushNotifications(item.data)) {
        statuses.push({ VAR_NAME: 'notify_via', VAR_VALUE: 'PUSH' });
      }
      this.statusSrv.setStatus(statuses).subscribe((successfully) => {
        if (successfully) {
          this.activeEndpointSrv.setActiveEndpoint(item.data);
          if (this.pushNotifications(item.data)) {
            this.toastSrv.showToast('Message notifications will be delivered via PUSH');
          }
        }
      });
    }
  }

  pushNotifications(status: EndpointGroupsInterface) {
    return status.sms_dest !== 1 && this.statuses()?.['notify_via'] === 'SMS';
  }

  pinManagementPage() {
    this.menuCtrl.close();
    this.router.navigate([`/${APP_ROUTES.PIN_MANAGEMENT}`]);
    this.events.publish('contact:reset-search');
  }

  blackListTab() {
    this.menuCtrl.close();
    this.router.navigate([`/${APP_ROUTES.HOME}/${HOME_ROUTES.BLACK_LIST}`]);
  }

  blockListTab() {
    this.menuCtrl.close();
    this.router.navigate([`/${APP_ROUTES.HOME}/${HOME_ROUTES.BLOCK_LIST}`]);
  }

  settingPage() {
    this.menuCtrl.close();
    this.router.navigate([`/${APP_ROUTES.SETTINGS}`]);
    this.events.publish('contact:reset-search');
  }

  conferencePage() {
    this.menuCtrl.close();
    this.router.navigate([`/${APP_ROUTES.CONFERENCES}`]);
    this.events.publish('contact:reset-search');
  }

  labelsPage() {
    this.menuCtrl.close();
    this.labelsSrv.openLabelsPage();
  }

  async exportAddresses($event: MouseEvent) {
    const interval = await this.downloadSrv.showIntervalPopover($event);
    if (interval) {
      this.menuCtrl.close();
      this.downloadSrv.downloadContactAddresses(interval).subscribe((blob) => {
        const filename = `Addresses_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xlsx`;
        saveAs(blob, filename);
      });
    }
  }

  protected verifyAddresses() {
    this.downloadSrv.downloadInvalidContacts().subscribe(async (blob) => {
      const confirm = await this.alertCtrl.create({
        header: 'Address Validation',
        message:
          'File with invalid contacts is ready for download. Please click the button below to download the file.',
        mode: 'ios',
        buttons: [
          {
            text: 'Download',
            handler: () => {
              const filename = `Invalid_Contacts_${format(new Date(), 'yyyyMMdd')}.xlsx`;
              saveAs(blob, filename);
            },
          },
        ],
      });
      await confirm.present();
    });
  }

  protected exportSwissAddresses() {
    this.downloadSrv.downloadSwissAddresses().subscribe((blob) => {
      const filename = `Post_Address_Validation_${format(new Date(), 'yyyyMMdd')}.xlsx`;
      saveAs(blob, filename);
    });
  }

  protected importSwissAddresses(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const fileToUpload = input.files[0];
    this.uploadSrv.uploadSwissAddresses(fileToUpload).subscribe(async (res) => {
      const modal = await this.modalCtrl.create({
        component: ImportAddressesResultComponent,
        backdropDismiss: false,
        componentProps: {
          response: res,
        },
      });

      this.menuCtrl.close();
      await modal.present();
    });
  }

  async logout() {
    const confirm = await this.alertCtrl.create({
      header: 'Abmelden?',
      message: 'Möchten Sie sich wirklich abmelden?',
      buttons: [
        { text: 'Nein' },
        {
          text: 'Ja',
          handler: () => {
            this.userSrv.logout();
            this.menuCtrl.close();
          },
        },
      ],
    });
    await confirm.present();
  }
}
